import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useMarketplace } from "../../context/MarketplaceContext";
import { getStandardPages } from "../../services/api";
import ProductSearch from "../product/ProductSearch";
import { motion, LazyMotion, domAnimation } from "framer-motion";
import { AnimatePresenceWithProps } from "../../types/animation";

interface StandardPage {
  slug: string;
  title: string;
}

// Icons
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const AdminHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentMarketplace } = useMarketplace();
  const navigate = useNavigate();
  const location = useLocation();
  const [standardPages, setStandardPages] = useState<StandardPage[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPagesMenuOpen, setIsPagesMenuOpen] = useState(false);
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);

  // Memoize the fetchStandardPages function to prevent it from being recreated on every render
  const fetchStandardPages = useCallback(async () => {
    try {
      const pages = await getStandardPages();
      setStandardPages(pages);
    } catch (error) {
      console.error("Failed to fetch standard pages:", error);
      // Set default pages on error to prevent repeated failed requests
      setStandardPages([]);
    }
  }, []); // No dependencies as this function doesn't rely on props or state

  // Only run fetchStandardPages once when the component mounts
  useEffect(() => {
    fetchStandardPages();
  }, [fetchStandardPages]); // Only fetchStandardPages as dependency, which is memoized

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    logout();
    navigate("/");
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Toggle mobile menu with stopPropagation to prevent immediate closure
  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileSearchVisible) setIsMobileSearchVisible(false); // Close search if opening menu
  };

  const toggleMobileSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMobileSearchVisible(!isMobileSearchVisible);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false); // Close menu if opening search
  };

  // Header classes - solid white background for admin pages
  const headerClasses =
    "site-header py-3 md:py-4 px-4 md:px-6 sticky top-0 bg-white z-50 transition-all duration-300 shadow-md";

  // Container classes
  const containerClasses =
    "container mx-auto flex justify-between items-center";

  // Logo color style - gradient applied
  const logoTextStyle = {
    backgroundImage: "linear-gradient(to right, #2563eb, #4f46e5)", // Brighter gradient: blue-600 to indigo-600
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent", // For Webkit browser compatibility
    color: "transparent", // Standard property
  };

  return (
    <LazyMotion features={domAnimation}>
      <header className={headerClasses}>
        <div className={containerClasses}>
          <AnimatePresenceWithProps mode="wait">
            {isMobileSearchVisible && (
              <motion.div
                key="mobileSearch"
                className="w-full flex items-center md:hidden"
                initial={{ opacity: 0, x: "100%" }}
                animate={{ opacity: 1, x: "0%" }}
                exit={{ opacity: 0, x: "100%" }}
                transition={{ duration: 0.3 }}
              >
                <ProductSearch
                  inlineButton={true}
                  className="flex-grow"
                  buttonText="Go"
                  placeholder="Search..."
                />
                <button
                  onClick={toggleMobileSearch}
                  className="p-2 ml-2 text-gray-500 hover:text-gray-700"
                  aria-label="Close search"
                >
                  <CloseIcon />
                </button>
              </motion.div>
            )}
          </AnimatePresenceWithProps>

          {!isMobileSearchVisible && (
            <>
              <Link
                to="/"
                className="flex items-center z-20 pl-0 md:pl-4 mr-auto md:mr-0"
              >
                <span
                  className="text-lg md:text-xl font-bold"
                  style={logoTextStyle}
                >
                  {currentMarketplace.name}
                </span>
              </Link>

              <div className="hidden md:block w-1/3 mx-4">
                <ProductSearch
                  inlineButton={true}
                  className="w-full"
                  buttonClassName="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                />
              </div>

              {/* Mobile Controls: Search Icon + Menu Hamburger */}
              <div className="md:hidden flex items-center z-20 pl-2 md:pl-0 space-x-1">
                <button
                  onClick={toggleMobileSearch}
                  className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label="Open search"
                >
                  <SearchIcon />
                </button>

                <button
                  onClick={toggleMobileMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  aria-expanded={isMobileMenuOpen}
                  aria-label="Toggle navigation menu"
                >
                  <span className="sr-only">Open main menu</span>
                  {isMobileMenuOpen ? (
                    <CloseIcon />
                  ) : (
                    <svg
                      className="h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  )}
                </button>
              </div>

              <div className="hidden md:flex items-center justify-end space-x-6">
                {user && (
                  <div className="relative">
                    <button
                      type="button"
                      className="flex items-center text-sm rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 px-3 py-2"
                      id="user-menu-button"
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="true"
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    >
                      <span className="sr-only">Open user menu</span>
                      Welcome, {user.email}
                    </button>

                    <AnimatePresenceWithProps>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.1 }}
                          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="user-menu-button"
                        >
                          <div className="py-1" role="none">
                            <Link
                              to="/dashboard"
                              className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                              role="menuitem"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              Dashboard
                            </Link>
                            <Link
                              to="/admin"
                              className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                              role="menuitem"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              Admin Panel
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              role="menuitem"
                            >
                              Sign out
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresenceWithProps>
                  </div>
                )}

                {!user && (
                  <div className="flex items-center space-x-4">
                    <Link
                      to="/login"
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors duration-200"
                    >
                      Sign up
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Mobile menu */}
        <AnimatePresenceWithProps>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white border-t border-gray-200"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                <Link
                  to="/"
                  className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/products"
                  className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Products
                </Link>

                {/* User menu for mobile */}
                {user && (
                  <>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center px-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-base font-medium text-gray-800">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <Link
                          to="/dashboard"
                          className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/admin"
                          className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Admin Panel
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="text-gray-900 hover:bg-gray-50 block w-full text-left px-3 py-2 rounded-md text-base font-medium"
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {!user && (
                  <div className="border-t border-gray-200 pt-4">
                    <Link
                      to="/login"
                      className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="text-gray-900 hover:bg-gray-50 block px-3 py-2 rounded-md text-base font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign up
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresenceWithProps>
      </header>
    </LazyMotion>
  );
};

export default AdminHeader;
